<template>
	<div
		class="product-groups product-tour-step-3"
		:class="{
			'parent-selected': parentSelected,
			'extended-list': isExtenedList,
			'show-all-lists': productGroupsState,
		}"
	>
		<div class="levels" :style="[isExtenedList ? getStyles : '']">
			<div class="level" v-for="(obj, level, index) of getChips" :key="index">
				<p class="labels body-1">
					<span v-for="(label, j) in obj.labels" :key="j">{{ label }}</span>
				</p>
				<div class="chips-wrap remove-prev remove-next">
					<div class="scroll-prev scroll-btn" @click.prevent="handleSliderClick($event, 'prev')">
						<base-icon class="next" icon-name="chevron-left" />
					</div>
					<ul class="slider values" @wheel.prevent="throttleMe">
						<li
							v-for="(val, k) in Object.keys(obj.values)"
							:key="`${k}-${obj.labels[index]}-${getSelectedProduct}`"
							class="value slide"
							:class="{current: k === 0, selected: chipIsSelected(level, val)}"
							v-on="!chipIsSelected(level, val) ? {click: () => updateSelectedProduct(level, val)} : null"
						>
							<Observer
								v-if="k === 0 || Object.keys(obj.values).length - 1 === k"
								class="observer"
								:class="k > 0 ? 'observer-last' : 'observer-first'"
								:threshold="0.5"
								:key="`observer-${k}-${obj.labels[index]}-${getSelectedProduct}`"
								@onChange="handelObsChange"
							>
								<md-button
									class="chips-button caption-2"
									:class="{selected: chipIsSelected(level, val)}"
									><span v-html="val"
								/></md-button>
							</Observer>
							<md-button
								v-else
								class="chips-button caption-2"
								:class="{selected: chipIsSelected(level, val)}"
								><span v-html="val"
							/></md-button>
						</li>
					</ul>
					<div class="scroll-next scroll-btn" @click.prevent="handleSliderClick($event, 'next')">
						<base-icon class="next" icon-name="chevron-right" />
					</div>
				</div>
			</div>
		</div>
		<div v-if="isExtenedList" class="extend-lists" @click.prevent="showAllLevels">
			<base-icon class="extend" icon-name="chevron-right" />
		</div>
	</div>
</template>

<script>
/* eslint-disable no-param-reassign */
import clonedeep from 'lodash.clonedeep';
import productGroupSlider from './mixins/productGroupSlider';
import {parseHTML} from '@/util/parseHTML.js';
import Observer from '../../../shared/Observer.vue';

export default {
	name: 'ProductGroups',
	components: {
		Observer,
	},
	mixins: [productGroupSlider],
	props: {
		productsInGroup: {
			type: Array,
		},
		currentProduct: {
			type: String,
		},
		parentSelected: {
			type: Boolean,
		},
		productGroupsState: {
			type: Boolean,
		},
	},
	data() {
		return {
			levelsLoaded: false,
			el: null,
		};
	},
	methods: {
		parseHTML,
		async init() {
			await this.$nextTick();
			this.levelsLoaded = true;
			this.setObserver();
		},
		addLevel(chipObj, key, {level, label, value}) {
			// add level if not there
			if (!(level in chipObj)) chipObj[level] = {labels: new Set(), values: {}};

			// add values if not there
			if (!(value in chipObj[level].values)) chipObj[level].values[value] = new Set();

			if (level === 6 && !label) label = 'size';
			// add info to Sets
			chipObj[`${level}`].labels.add(this.$store.getters.getPclLabel(label));
			chipObj[`${level}`].values[value].add(key);

			return chipObj;
		},
		sortChipsLevel(chips) {
			const findIndex = (val) =>
				this.getProductsGroup.findIndex((j, i, arr) =>
					arr[i].productConfigurator.some((el) => el.value === val),
				);

			for (const lvl in chips) {
				if (!chips[lvl]) continue;
				chips[lvl].labels = new Set([...chips[lvl].labels].sort());
				if (lvl === '1') {
					chips[lvl].values = Object.fromEntries(
						Object.entries(chips[lvl].values).sort((a, b) => findIndex(a[0]) - findIndex(b[0])),
					);
				} else {
					chips[lvl].values = Object.fromEntries(
						Object.entries(chips[lvl].values).sort((a, b) =>
							a[0].localeCompare(b[0], undefined, {
								numeric: true,
								sensitivity: 'base',
							}),
						),
					);
				}
			}
			return chips;
		},
		setObserver() {
			const obs = this.$children.filter((child) => child.$options.name === 'Observer');
			if (!obs) return;
			// set up the slider/observe for lists that overflow the parent container
			for (const ob of obs) {
				const slider = ob.$el.closest('.slider');
				if (slider.parentNode.clientWidth >= slider.scrollWidth) continue;
				slider.closest('.chips-wrap').classList.add('show-slider-arrow');
			}
			this.scrollToSelected();
		},
		showAllLevels() {
			this.$emit('extendProductGroups');
		},
		chipIsSelected(level, val) {
			this.setObserver();
			return this.getChips[level].values[val]?.has(this.getSelectedProduct);
		},
		updateSelectedProduct(level, val) {
			for (const key of this.getChips[level].values[val].values())
				if (key !== this.currentProduct) return this.$emit('chipSelected', key);
		},
		getLevelConfig(level, productConfigurator) {
			return productConfigurator.find((g) => g?.level === level) || {};
		},
		handelObsChange(obs, kill) {
			const {target} = obs;
			const chipsWrap = target.closest('.show-slider-arrow');

			if (!chipsWrap) return kill();

			const direction = target.classList.contains('observer-last') ? 'remove-next' : 'remove-prev';

			if (obs.isIntersecting) chipsWrap.classList.add(direction);
			else chipsWrap.classList.remove(direction);
		},
	},
	computed: {
		getChips() {
			const productGroups = clonedeep(this.getProductsGroup);
			const {key: selectedKey, productConfigurator: selectedConfig} = productGroups.splice(
				productGroups.findIndex((product) => product.key === this.getSelectedProduct),
				1,
			)[0];

			let chips = {};
			let prevLevel;

			// set selected config first
			// cache selected chips
			for (const config of selectedConfig) chips = this.addLevel(chips, selectedKey, config);

			// 1 - 6 go through all of the other products
			// eslint-disable-next-line guard-for-in
			for (let i in [...Array(6)]) {
				++i; // update i to match level numbering

				for (const {key, productConfigurator} of productGroups) {
					// get current level
					const {level, label, value} = this.getLevelConfig(i, productConfigurator);
					if (!level) continue;

					// always add all values for level one
					if (level === 1) chips = this.addLevel(chips, key, {level, label, value});
					// add next level if previous values match selected previous levels values
					else if (prevLevel) {
						// check if every level above this level, matches the selected levels value
						const currChipLevels = Object.keys(chips)
							.filter((chip) => chip < level)
							.every((chipLvl) => {
								const selProd = this.getLevelConfig(+chipLvl, selectedConfig);
								const curProd = this.getLevelConfig(+chipLvl, productConfigurator);
								return selProd.value === curProd.value;
							});
						if (currChipLevels) chips = this.addLevel(chips, key, {level, label, value});
					} else if (!prevLevel) {
						// check if both level 1s share the same value
						const selLevel1 = this.getLevelConfig(1, selectedConfig);
						const currLevel1 = this.getLevelConfig(1, productConfigurator);
						if (selLevel1.value === currLevel1.value)
							chips = this.addLevel(chips, key, {level, label, value});
					} else continue;
				}

				if (i in chips && Object.keys(chips[i].values).length > 1) {
					prevLevel = i;
				}
				// remove levels that have only one value
				else {
					delete chips[i];
				}
			}
			return this.sortChipsLevel(chips);
		},
		isExtenedList() {
			// checks if group has more than 3 levels
			return Object.keys(this.getChips)?.length > 3;
		},
		getSelectedProduct() {
			return this.currentProduct;
		},
		getObserverRoot() {
			if (!this.getEl) return;
			return this.getEl.closest('.product-groups');
		},
		getStyles() {
			// sets max height for 'show all levels' animation
			if (!this.getEl) return;
			const style = {maxHeight: 'none'};
			const levels = this.getEl.querySelectorAll('.level');
			const count = !this.productGroupsState ? [3, 0] : [levels.length, 50];
			const levelHeight = [...Array(count[0]).keys()].reduce((a, b) => a + levels[b].offsetHeight, count[1]);
			style.maxHeight = `${levelHeight}px`;
			return style;
		},
		getProductsGroup() {
			return this.productsInGroup;
		},
		getEl() {
			return this.el;
		},
	},
	async mounted() {
		this.el = this.$el;
		await this.$nextTick();
		this.init();
	},
};
</script>

<style lang="scss" scoped>
.product-groups {
	position: relative;
	counter-reset: level-counter;
	.levels {
		overflow: hidden;
		transition: max-height 0.5s ease-in-out;
	}
	.level {
		padding-bottom: 12px;
		&:not(:nth-child(1)) {
			border-top: 1px solid $color-extra-light-blue;
			padding-top: 12px;
		}
		.labels {
			margin: 0;
			display: flex;
			flex-wrap: nowrap;
			color: $color-text-grey;
			margin-bottom: 6px;

			span {
				text-transform: capitalize;
				&:not(:nth-child(1)) {
					&:before {
						content: '/';
						padding: 0 2px;
					}
				}
			}
			&:before {
				padding-right: 4px;
				counter-increment: level-counter;
				content: counter(level-counter) '. ';
			}
		}
		.chips-wrap {
			display: flex;
		}
		.slider {
			padding: 0;
			margin: 0;
			list-style-type: none;
			width: calc(100% + 40px);
			display: flex;
			flex-wrap: nowrap;
			scrollbar-width: none;
			overflow: -moz-scrollbars-none;
			-ms-overflow-style: none;
			-webkit-overflow-scrolling: touch;
			scroll-behavior: smooth;
			transform: translate(0, 0);
			transition: transform 0.5s ease;
			&:active {
				cursor: grabbing;
			}
			&::-webkit-scrollbar {
				width: 0 !important;
				display: none;
			}
			li {
				padding-right: 8px;
			}
			.place-holder {
				min-width: 15px;
			}

			button::v-deep.md-button.md-theme-default {
				@include font-styles('Futura W01 Medium', 500);
				@include textStyles(16, 23, 0.4);
				color: $color-text-grey;
				border: 1px solid $color-grey;
				border-radius: 8px;
				.md-ripple {
					padding: 5px 8px 4px;
					.md-ripple-wave {
						background-color: $color-blue;
					}
				}
				&:hover,
				&:active,
				&:focus {
					background-color: $color-super-light-blue;
					&:before {
						background-color: inherit;
					}
				}
				&.selected {
					border-color: $color-blue;
					color: $color-blue;
					background-color: $color-super-light-blue;
				}
			}
		}
	}
	.scroll-next,
	.scroll-prev {
		display: none;
	}

	.show-slider-arrow {
		.scroll-btn {
			display: flex;
			position: absolute;
			width: 30px;
			height: 34px;
			justify-content: center;
			align-items: center;
			color: $color-blue;
			z-index: 10;
			transition-delay: 0s;
			opacity: 1;
			transition: background 0.2s, opacity 0.5s ease 0s;
			i {
				pointer-events: none;
			}
		}
		&.remove-prev .scroll-prev {
			opacity: 0;
		}
		&.remove-next .scroll-next {
			opacity: 0;
		}
		.scroll-next {
			background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 40%);
			right: -20px;
		}
		.scroll-prev {
			background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, #ffffff 40%);
			left: -20px;
		}
	}
	&.parent-selected {
		.scroll-next {
			background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, $color-super-light-blue 40%);
		}
		.scroll-prev {
			background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, $color-super-light-blue 40%);
		}
	}
	&.extended-list {
		&:not(.show-all-lists) .level {
			&:nth-child(n + 3) {
				.scroll-btn {
					transition: background 0.2s, opacity 0.5s ease 0.3s;
					opacity: 0;
				}
			}
		}

		.extend-lists {
			position: absolute;
			bottom: 0;
			left: 0;
			display: flex;
			width: 100%;
			height: 50px;
			align-items: flex-end;
			justify-content: center;
			background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%);
			z-index: 11;
			transition: 0.3s ease, background 0s;
			.extend {
				transition: all 0.3s ease;
				transform: rotateX(0deg) rotateZ(90deg);
				color: $color-blue;
				pointer-events: none;
			}
		}
		&.show-all-lists {
			.level {
				.scroll-btn {
					transition: background 0.2s, opacity 0.5s ease 0s;
				}
			}
			.extend-lists {
				height: 20px;
				position: relative;
				.extend {
					transition: all 0.3s ease;
					transform: rotateX(-180deg) rotateZ(90deg);
				}
			}
		}
		&.parent-selected {
			.extend-lists {
				background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, $color-super-light-blue 50%);
			}
		}
	}
}
</style>
